import { useContext, useEffect, useState, useTransition } from "react";

import { isValidPositiveNumber, Log } from "@ryde/shared";

import { DropdownSourceContext } from "../../contexts";

export interface ISupplierProviderState {
  /** The supplier's external provider (e.g. "YANGO"), undefined when it has none or is not resolved yet. */
  provider: string | undefined;
  /**
   * False while the "supplier" dropdown source is still being read for the current supplierId, so
   * `provider` carries no information yet. Callers gating an action on the provider must treat this as
   * "unknown" and stay conservative (e.g. keep Send disabled) instead of reading undefined as
   * "not a taxi provider" — otherwise the action is briefly allowed before the lookup lands.
   */
  isResolved: boolean;
}

/** No supplier selected: there is no provider to look up, so the state is already final. */
const NO_SUPPLIER_PROVIDER_STATE: ISupplierProviderState = {
  provider: undefined,
  isResolved: true,
};

const RESOLVING_SUPPLIER_PROVIDER_STATE: ISupplierProviderState = {
  provider: undefined,
  isResolved: false,
};

/**
 * Resolves the external provider (e.g. "YANGO") of a supplier from the loaded "supplier" dropdown
 * source. Used to gate the ride Send action live when a past ride is assigned to a taxi provider
 * (RYDE-7633).
 */
export const useSupplierProvider = (
  supplierId: number | undefined,
): ISupplierProviderState => {
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const [providerState, setProviderState] = useState<ISupplierProviderState>(
    RESOLVING_SUPPLIER_PROVIDER_STATE,
  );
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (!isValidPositiveNumber(supplierId)) {
      setProviderState(NO_SUPPLIER_PROVIDER_STATE);
      return;
    }

    // Reset synchronously: until the lookup for THIS supplierId lands, a provider resolved for the
    // previously selected supplier must not be reported as this one's.
    setProviderState(RESOLVING_SUPPLIER_PROVIDER_STATE);

    let isStaleRequest = false;

    const fetchProvider = async (): Promise<void> => {
      try {
        const items = await getDropdownItems("supplier");
        const item = items.find(x => Number(x.value) === supplierId);

        if (isStaleRequest) {
          return;
        }

        setProviderState({ provider: item?.sendToProvider, isResolved: true });
      } catch (error) {
        // Stay unresolved so callers keep their conservative default rather than reading a failed
        // lookup as "this supplier has no provider".
        Log.error("Failed to resolve supplier provider", error);
      }
    };

    startTransition(() => {
      void fetchProvider();
    });

    return () => {
      isStaleRequest = true;
    };
  }, [supplierId, getDropdownItems]);

  return providerState;
};
